import {
  DeviceDashboardDto,
  ParameterDto,
  ParameterDtoPreferred,
} from '../../services/deviceApi';
import React, { useContext, useState } from 'react';
import '../../styles/pages/device-card.css';
import HelperFunctions, { getDeviceStatusLabel } from '../../utils/helperFunctions';
import { ArrowRight, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { UserSelectedContext } from '../../contexts/UserSelectedContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

/**
 * DeviceCard Component
 *
 * A mobile/tablet optimized device card component that displays device information
 * in a visually appealing card format with device image, status, and metrics.
 *
 * Props:
 * - device: Device object containing all device information
 * - onClick: Optional click handler for the card
 *
 * Usage:
 * <DeviceCard device={deviceData} onClick={() => navigate('/device-detail')} />
 *
 * Features:
 * - Visual device representation with background image
 * - Real-time status indicator
 * - Device metrics display
 * - Responsive design for mobile and tablet
 */

interface DeviceCardProps {
  devices: DeviceDashboardDto[];
  parameters: ParameterDtoPreferred[];
  onDeviceClick?: (_deviceId: number) => void;
  handleDeleteDevice: (_id: number) => void;
  demoDeviceId: number | null;
  demoUserId: number | null;
}

const DeviceCard: React.FC<DeviceCardProps> = ({
  devices,
  parameters,
  onDeviceClick,
  handleDeleteDevice,
  demoDeviceId,
  demoUserId
}) => {
  // const [shownNonAqiParametersForDevice, setShownNonAqiParametersForDevice] = useState<number>(0);

  // const handleShowNonAqiParameters = (e: React.MouseEvent<HTMLDivElement>, deviceId: number) => {
  //   e.stopPropagation();
  //   if(shownNonAqiParametersForDevice === deviceId) {
  //     setShownNonAqiParametersForDevice(0);
  //   } else {
  //     setShownNonAqiParametersForDevice(deviceId);
  //   }
  // }
  const navigate = useNavigate();
  const [popUpDeviceId, setPopUpDeviceId] = useState<number | null>(null)

  /*Context for hiding bottom bar*/
  const userSelectedContext = useContext(UserSelectedContext);
  if (!userSelectedContext) {
    throw new Error('useUserSelected must be used within a UserSelectedProvider');
  }
  const { hideBottomBar } = userSelectedContext;

  const { user } = useAuth();

  // WiFi status state - maps deviceId to WiFi status type
  // const [wifiStatusMap, setWifiStatusMap] = useState<Record<number, 'warning' | 'connected' | 'offline' | null>>({});

  // Fetch WiFi status for all devices
  // useEffect(() => {
  //   const fetchWiFiStatuses = async () => {
  //     const statusMap: Record<number, 'warning' | 'connected' | 'offline' | null> = {};
      
  //     await Promise.all(
  //       devices.map(async (device) => {
  //         if (device.serialNumber) {
  //           try {
  //             const { deviceApi } = await import('../../services/deviceApi');
  //             const deviceInfo = await deviceApi.getDeviceBySerialNumber(device.serialNumber);
  //             const onboardingStatus = (deviceInfo as any).onboarding_status || deviceInfo.onboardingStatus;
  //             const operationalStatus = (deviceInfo as any).operational_status || (deviceInfo as any).operationalStatus;
              
  //             if (onboardingStatus !== undefined && onboardingStatus !== null) {
  //               const onboardingUpper = onboardingStatus.toUpperCase();
  //               const operationalUpper = operationalStatus ? operationalStatus.toUpperCase() : 'UNKNOWN';
                
  //               // Show WiFi warning icon when REGISTERED + UNKNOWN (Wi-Fi not configured)
  //               if (onboardingUpper === 'REGISTERED' && operationalUpper === 'UNKNOWN') {
  //                 statusMap[device.deviceId] = 'warning';
  //               } 
  //               // Show WiFi warning icon when WIFICONNECTED + UNKNOWN (waiting for data/status unknown)
  //               else if (onboardingUpper === 'WIFICONNECTED' && operationalUpper === 'UNKNOWN') {
  //                 statusMap[device.deviceId] = 'warning';
  //               }
  //               // Show regular WiFi icon when WIFICONNECTED + ONLINE (Internet connected)
  //               else if (onboardingUpper === 'WIFICONNECTED' && operationalUpper === 'ONLINE') {
  //                 statusMap[device.deviceId] = 'connected';
  //               }
  //               // Show WiFi-off icon when WIFICONNECTED + OFFLINE (Wi-Fi lost / no internet)
  //               else if (onboardingUpper === 'WIFICONNECTED' && operationalUpper === 'OFFLINE') {
  //                 statusMap[device.deviceId] = 'offline';
  //               } 
  //               else {
  //                 statusMap[device.deviceId] = null;
  //               }
  //             } else {
  //               statusMap[device.deviceId] = null;
  //             }
  //           } catch (error) {
  //             console.error(`Error fetching WiFi status for device ${device.deviceId}:`, error);
  //             statusMap[device.deviceId] = null;
  //           }
  //         } else {
  //           statusMap[device.deviceId] = null;
  //         }
  //       })
  //     );
      
  //     setWifiStatusMap(statusMap);
  //   };

  //   if (devices.length > 0) {
  //     fetchWiFiStatuses();
  //   }
  // }, [devices]);

  // Getting formatted parameter value
  const GetParamaterValue = (parameter: ParameterDto | undefined) => {
    if(parameter === undefined || parameter.value === null || parameter.value === undefined) {
      return "N/A";
    }
    if(parameter.parameterName === 'AQI') {
      return Number.isInteger(parameter.value) ? parameter.value : parameter.value.toFixed(0);
    }
    if(parameter.parameterName === 'Temperature' || parameter.parameterName === 'Humidity') {
      return Math.round(parameter.value);
    }
    return Number.isInteger(parameter.value) ? parameter.value : parameter.value.toFixed(2);
  }

  if(devices.length === 0) {
    return (
      <div className="text-center text-[var(--color-sub-title-text)] text-base font-medium">No data found</div>
    );
  }

  const GetStatusText = (parameter: ParameterDto | undefined) => {
    if(parameter === undefined || parameter.value === null || parameter.value === undefined) {
      return '';
    }
    const statusText = HelperFunctions.GetStatusText(parameter.value, parameter.parameterName);
    return statusText.length > 9 ? statusText.slice(0, 6) + '...' : statusText;
  }

  const deviceCardParametersList = ['PM10', 'PM2.5', 'CO₂', 'VOC', 'Temperature', 'Humidity'];

  /* AQI Parameters: PM10, PM2.5, O₃, NO₂, CO */
  const deviceCardParameters = parameters.filter(parameter => deviceCardParametersList.includes(parameter.parameterName)).sort((a, b) => deviceCardParametersList.indexOf(a.parameterName) - deviceCardParametersList.indexOf(b.parameterName));
  
  /* Non AQI Parameters: Temperature, Humidity, etc. */
  // const nonAqiParameters = parameters.filter(parameter => parameter.parameterName !== 'AQI' && !aqiParameters.includes(parameter));

  const handleDeviceCardClick = (deviceId: number) => {
    hideBottomBar();
    onDeviceClick?.(deviceId);
  }


  return (
    <>
      {devices.map(device => {
        const aqiParameter = Object.values(device.parameters).find(
          (p: ParameterDto) => p.parameterName === 'AQI'
        );

        return (
          <div
            className="device-card"
            key={device.deviceId}
            onClick={() => handleDeviceCardClick(device.deviceId)}
          >
            {/* Information Section */}
            <div className="device-card-header">
              <div className="device-card-info">
                {/* Visual Section with Device Image */}
                <div className="device-card-visual">
                  <div
                    className={`device-card-aqi ${GetParamaterValue(aqiParameter) === 'N/A' ? 'aqi-na' : ''}`}
                    onClick={e => e.stopPropagation()}
                  >
                    <span>AQI</span>
                    <span
                      style={{
                        color: HelperFunctions.GetParameterColor(
                          aqiParameter?.value,
                          'AQI'
                        ),
                      }}
                    >
                      {GetParamaterValue(aqiParameter)}{' '}
                      {GetStatusText(aqiParameter)}
                    </span>
                    {HelperFunctions.GetStatusText(aqiParameter?.value, aqiParameter?.parameterName).length > 9 && <div
                      className="device-card-aqi-status-tooltip"
                    >
                      {HelperFunctions.GetStatusText(
                        aqiParameter?.value,
                        'AQI'
                      )}
                    </div>}
                  </div>
                  {/* <img
                    src={
                      device.deviceImageUrl ||
                      `${process.env.PUBLIC_URL}/DashboardWelcome/DeviceImage.png`
                    }
                    alt="Device environment"
                    className="device-card-bg-image"
                    onError={e => {
                      e.currentTarget.src =
                        `${process.env.PUBLIC_URL}/DashboardWelcome/DeviceImage.png`;
                    }}
                  /> */}
                  <div className="device-identification">
                    <div className="flex items-center gap-2">
                      <h3 className="device-card-name">{device.deviceName}</h3>
                        {/* Wi-Fi warning icon hidden */}
                        {/* {device.onboardingStatus == 'REGISTERED' &&
                        device.operationalStatus == 'UNKNOWN' && (
                          <div
                            className="flex items-center gap-1"
                            title="Wi-Fi not configured / waiting for data"
                          >
                            <Wifi className="h-4 w-4 text-[var(--color-text)]" />
                            <span className="text-[var(--color-text)] font-bold text-sm leading-none">
                              !
                            </span>
                          </div>
                        )} */}
                       {/*device.onboardingStatus == 'WIFICONNECTED' &&
                        device.operationalStatus == 'ONLINE' && (
                          <div
                            className="flex items-center"
                            title="Wi-Fi connected"
                          >
                            <Wifi className="h-4 w-4 text-[var(--color-text)]" />
                          </div>
                        )*/}
                      {/*device.onboardingStatus == 'WIFICONNECTED' &&
                        device.operationalStatus == 'OFFLINE' && (
                          <div
                            className="flex items-center"
                            title="Wi-Fi connected but offline"
                          >
                            <WifiOff className="h-4 w-4 text-[var(--color-text)]" />
                          </div>
                        )*/}
                    </div>
                    {/* <div className="device-card-model-environment">
                      {/* <p className="device-card-model"><span className='text-[var(--color-text)]'>Model Number : </span>{device.deviceModel}</p> */}
                      {/* <div className="device-card-environment-mode">
                        {device.environmentMode != null ? <img src={`${process.env.PUBLIC_URL}/EnvironmentModeIcons/${device.environmentMode}.svg`} alt={device.environmentMode} className="w-full h-full object-contain" /> : '--'}
                        </div>
                    </div> */}
                  </div>
                </div>
              </div>

              <div className="device-card-right-section">
                <div className='flex items-center gap-2'>
                  {/* Status Badge */}
                  {(() => {
                    const { label, variant } = getDeviceStatusLabel(device.isActive, device.operationalStatus);
                    return (
                      <div className={`device-status-badge ${variant}`}>
                        {label}
                      </div>
                    );
                  })()}

                  {/* Device 3 dots for tab to edit and delete */}
                  <div
                    onClick={e => {
                      e.stopPropagation();
                      if (demoDeviceId == device.deviceId && demoUserId != user?.userId) return;
                      setPopUpDeviceId(device.deviceId);
                      if (popUpDeviceId == device.deviceId) {
                        setPopUpDeviceId(null);
                      }
                    }}
                    className="three-dots p-1.5 hover:bg-[var(--button-hover)] rounded-full transition-colors"
                  >
                    <MoreVertical className="w-4 h-4 text-[var(--color-text)]" />
                  </div>
                </div>

                {/* Device model number */}
                <p className="device-card-model"><span className="text-[var(--color-text)]">Model: </span><span className="text-[var(--color-text)]">{device.deviceModel}</span></p>
              </div>
            </div>

            {/* Metrics Row */}
            <div className="device-metrics">
              {/* AQI Parameters: PM10, PM2.5, O₃, NO₂, CO */}
              {deviceCardParameters.map(parameter => (
                <div className="metric-item" key={parameter.driverID}>
                  <span className="metric-label">
                    {parameter.parameterName == 'Temperature'
                      ? 'Temp'
                      : parameter.parameterName == 'Humidity'
                        ? 'Hum'
                        : parameter.parameterName}
                    :
                  </span>
                  <span className="metric-value">
                    {GetParamaterValue(device.parameters[parameter.driverID])}
                    {device.parameters[parameter.driverID]?.value != null &&
                    parameter.unit && (
                      <span className="metric-unit"> {parameter.unit}</span>
                    )}
                  </span>
                </div>
              ))}

              {/* Non AQI Parameters: Temperature, Humidity, etc. */}
              {/* {shownNonAqiParametersForDevice === device.deviceId && nonAqiParameters
                .map(parameter => (
                  <div className="metric-item" key={parameter.driverID}>
                    <span className="metric-label">
                      {parameter.parameterName == 'Temperature' ? 'Temp' : parameter.parameterName}:
                    </span>
                    <span className="metric-value">
                      {GetParamaterValue(device.parameters[parameter.driverID])} <span className="metric-unit">{device.parameters[parameter.driverID]?.value != null && parameter.unit ? `${parameter.unit}` : ''}</span>
                    </span>
                  </div>
                ))} */}

              {/* Show More Button for Non AQI Parameters */}
              {/* <div className='show-more-btn' onClick={(e) => handleShowNonAqiParameters(e, device.deviceId)}>
                  {shownNonAqiParametersForDevice === device.deviceId ? 'Show Less' : 'Show More'}
                </div> */}
              <div className="show-more-btn-container">
                {/* Device last updated value */}
                <div className="device-card-last-updated">
                  <span>Updated on</span>{' '}
                  {HelperFunctions.GetLastUpdated(device?.lastUpdated || '')}
                </div>
                {/* Show device details page button */}
                <div
                  className="card-right-arrow-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeviceCardClick(device.deviceId);
                  }}
                >
                  <ArrowRight className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>



            {/* External Dropdown */}
            {popUpDeviceId && popUpDeviceId === device.deviceId && (
              <div
                className="absolute right-4 top-14 w-40 bg-[var(--modal-background)] rounded-[0.625rem] border border-[var(--button-border-color)] z-[9999]"
                onMouseDown={e => e.stopPropagation()}
              >
                <div className="p-2">
                  <button
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPopUpDeviceId(null);
                      navigate(`/edit-device/${popUpDeviceId}`);
                    }}
                    className="w-full p-2 rounded-sm font-normal text-left text-xs text-[var(--color-sub-title-text)] hover:bg-[var(--button-hover)] flex items-center gap-3"
                  >
                    <Pencil className="w-4 h-4 text-[var(--color-sub-title-text)]" />
                    Edit Details
                  </button>
                  <button
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setPopUpDeviceId(null);
                      handleDeleteDevice(popUpDeviceId);
                    }}
                    className="w-full p-2 rounded-sm text-left font-normal text-xs text-[#E67E7D] hover:bg-[var(--button-hover)] flex items-center gap-3"
                  >
                    <Trash2 className="w-4 h-4 text-[#E67E7D]" />
                    Remove Device
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

export default DeviceCard;
